import {
  CSSProperties,
  Dispatch,
  HTMLProps,
  JSX,
  memo,
  ReactNode,
  RefObject,
  SetStateAction,
  useEffect,
  useRef,
} from 'react';
import { ActionMeta, components, FilterOptionOption, InputActionMeta } from 'react-select';

import TruncatedCreatableMultiReactSelect from '@ui/select/react-select/truncated-creatable-multi-react-select/truncated-creatable-multi-react-select';
import {
  ReactSelectInstance,
  ReactSelectInstanceRefObject,
  TReactSelectOption,
} from '@ui/select/react-select/custom-react-select.types';
import Container from '@ui/container/container';
import { reactSelectAdminStyles } from '@ui/select/react-select/react-select-styles';
import { ContainerProps } from '@ui/container/container.types';

import styles from './edit-state-wrapper.module.css';
import { TextProps } from '@ui/text/text/text.types';

interface IEditStateSelectWrapperProps<T = unknown, U = unknown, V = unknown> {
  ref?: ReactSelectInstanceRefObject;
  dynamicMenuPlacement?: boolean;
  dynamicMenuPlacementOffset?: number;
  label?: string;
  placeholder?: ReactNode;
  disabled?: boolean;
  menuListMaxHeight?: CSSProperties['maxHeight'];
  defaultValue?: TReactSelectOption<T, U, V>[];
  /** only pass in value if you want the component to be controlled */
  value?: TReactSelectOption<T, U, V>[];
  loading?: boolean;
  controlWidth?: CSSProperties['width'];
  viewableItems?: number | 'auto';
  onMenuClose?: (selectedOptions: TReactSelectOption[]) => void;
  onChange?: (selectedOptions: TReactSelectOption[], actionMeta: ActionMeta<unknown>) => void;
  options?: TReactSelectOption[];
  id?: string;
  className?: string;
  containerRef?: RefObject<HTMLDivElement | null>;
  isFocusedWithin?: boolean;
  setIsFocusedWithin?: Dispatch<SetStateAction<boolean>>;
  Components?: {
    UnfocusedCustomSelectedOptionComponent?: React.FC<{
      option: TReactSelectOption;
    }>;
  } & Partial<typeof components>;
  onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
  isLoading?: boolean;
  loadingMessage?: () => JSX.Element;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  isCreatable?: boolean;
  cannotClearOptions?: TReactSelectOption[];
  onCannotClearOptions?: (options: TReactSelectOption[]) => void;
  isMulti?: boolean;
  recalculateViewableItems?: number;
  noOptionsMessage?: string;
  filterOptions?: ((option: FilterOptionOption<unknown>, inputValue: string) => boolean) | null;
  styles?: ReturnType<typeof reactSelectAdminStyles>;
  unfocusedMultiValueContainerProps?: Partial<ContainerProps>;
  unfocusedMultiValueImageProps?: HTMLProps<HTMLImageElement>;
  unfocusedMultiValueTextProps?: Partial<TextProps>;
  unfocusedMultiValuePlusMoreContainerProps?: Partial<ContainerProps>;
  focusedMultiValueContainerProps?: Partial<ContainerProps>;
  virtualized?: boolean;
}

const EditStateSelectWrapper = memo(
  <T, U, V>({
    dynamicMenuPlacement = true,
    placeholder,
    disabled,
    controlWidth = '100%',
    viewableItems,
    containerRef,
    ref,
    isCreatable = false,
    value,
    defaultValue,
    ...rest
  }: IEditStateSelectWrapperProps<T, U, V>) => {
    const selectRef = useRef<ReactSelectInstance>(null);

    useEffect(() => {
      const select = selectRef?.current;
      const containerEl = containerRef?.current;
      if (!containerEl || !select) return;

      const handleContainerClick = () => {
        select.focus();
      };

      containerEl.addEventListener('click', handleContainerClick);

      return () => {
        containerEl.removeEventListener('click', handleContainerClick);
      };
    }, [containerRef]);

    return (
      <Container
        height="40px"
        width="100%"
        className={`flex ai-center ${styles.selectWrapper}`}
        data-component="EditStateSelectWrapper"
      >
        <TruncatedCreatableMultiReactSelect
          showHeader={false}
          ref={ref ? [selectRef, ref] : selectRef}
          dynamicMenuPlacement={dynamicMenuPlacement}
          placeholder={!disabled ? placeholder : ''}
          disabled={disabled}
          controlWidth={controlWidth}
          viewableItems={viewableItems === 'auto' ? undefined : viewableItems}
          autoCalculateViewableItems={viewableItems === 'auto'}
          isCreatable={isCreatable}
          value={value as TReactSelectOption[] | undefined}
          defaultValue={defaultValue as TReactSelectOption[] | undefined}
          {...rest}
        />
      </Container>
    );
  }
);

export default EditStateSelectWrapper;
